import _ from "lodash";
import React, { useState, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Link } from "react-router-dom";
import { MDBIcon } from "mdbreact";

import { useAuth } from "hooks/useAuth.js";
import useModal from "hooks/useModal";
import { toggleContact } from "actions/contacts/contactActions";

import Section from "components/common/Section";
import Table from "components/common/tables/Table";
import Modal from "components/common/Modal";
import EmptyList from "components/common/EmptyList";
import ContactDelete from "./ContactDelete";

import { isUserNotReadOnly } from "utils/authUtils";
import { filterTableColumns } from "utils/tableUtils";

const ContactList = () => {
  // hooks
  const {
    modalOpen,
    headerText,
    setHeaderText,
    formId,
    setFooter,
    setActions,
    selected,
    setSelected,
    setModalState
  } = useModal();
  const auth = useAuth();
  const dispatch = useDispatch();
  // redux hooks
  const { contactsAvailable, lastAction } = useSelector(
    state => state.contacts
  );

  // local state
  const [tableData, setTableData] = useState({
    columns: [
      {
        label: "#",
        field: "id",
        sort: "desc"
      },
      {
        label: "Name",
        field: "last_name",
        appendField: "first_name",
        sort: "desc",
        minimal: "sm"
      },
      {
        label: "Email",
        field: "email",
        sort: "desc",
        minimal: "sm"
      },
      {
        label: "City",
        field: "city",
        sort: "desc",
        minimal: "sm"
      },
      {
        label: "State",
        field: "state",
        sort: "desc",
        minimal: "sm"
      },
      {
        label: "Zip",
        field: "zip",
        sort: "desc",
        minimal: "sm"
      },
      {
        label: "Country",
        field: "country.name",
        sort: "desc",
        minimal: "sm"
      },
      {
        label: "Site",
        field: "org.name",
        sort: "desc",
        minimal: "sm"
      },
      {
        label: "Available",
        field: "is_available",
        toggleControl: true,
        sort: "disabled",
        minimal: "sm"
      },
      {
        label: "",
        field: "buttons",
        sort: "disabled"
      }
    ]
  });

  // remove available column once we know the user context is loaded
  // and it is not an admin
  if (auth.hasUserInfo && !auth?.user?.is_admin) {
    filterTableColumns(tableData, setTableData, ["is_available"]);
  }

  const toggleModal = (e, type, item) => {
    if (e) e.preventDefault();

    if (type === "delete") {
      setHeaderText("Delete Contact");
      setFooter(true);
      setActions([
        {
          text: "Delete",
          color: "danger",
          additionalProps: {
            form: formId,
            type: "submit"
          }
        }
      ]);
      setSelected(item);
    }

    if (type) {
      setModalState(true);
    } else {
      setModalState(false);
    }
  };

  const fetchTableLink = useCallback((type, item) => {
    switch (type) {
      case "show":
        return `/contacts/${item.id}`;
      case "edit":
        return `/contacts/${item.id}/edit`;
      default:
        return null;
    }
  }, []);

  const handleAvailabilityChange = (item, toggle) => {
    dispatch(toggleContact(item, toggle));
  };

  const sectionHeaderMenu = {
    order: 2,
    show: true,
    linkItems: [
      {
        name: (
          <>
            <MDBIcon icon="fas fa-plus" /> Add Contact
          </>
        ),
        url: "/contacts/new",
        additionalProps: {
          active: true
        }
      }
    ]
  };

  const renderTable = () => {
    if (_.size(contactsAvailable)) {
      return (
        <Table
          section="Contact"
          tableData={tableData}
          setTableData={setTableData}
          rowData={contactsAvailable}
          sort={["id", "desc"]}
          fetchTableLink={fetchTableLink}
          toggleAvailability={handleAvailabilityChange}
          toggleModal={toggleModal}
          updatedState={lastAction}
          clickableRow
          downloadableCSV
          linkToPage
          showButton
          editButton={isUserNotReadOnly(auth?.user?.user_level) ?? false}
          deleteButton={auth?.user?.is_admin ?? false}
        />
      );
    } else if (_.isEmpty(contactsAvailable)) {
      return (
        <EmptyList
          name="contact"
          namePlural="contacts"
          userRole={auth?.user?.user_level}
          createElement={<Link to="/contacts/new">Create</Link>}
        />
      );
    }
    return "";
  };

  return (
    <Section
      header="Contact List"
      menu={
        isUserNotReadOnly(auth?.user?.user_level) ? sectionHeaderMenu : null
      }
    >
      {renderTable()}
      {auth?.user?.is_admin ? (
        <Modal
          isOpen={modalOpen}
          toggle={toggleModal}
          headerText={headerText}
          includeFooter={true}
          actionButtons={[
            {
              text: "Delete",
              color: "danger",
              additionalProps: {
                form: formId,
                type: "submit"
              }
            }
          ]}
        >
          <ContactDelete
            formId={formId}
            selected={selected}
            closeModal={() => setModalState(false)}
          />
        </Modal>
      ) : (
        ""
      )}
    </Section>
  );
};

export default ContactList;
